// tokens.jsx — design tokens for Lift Log
// Aesthetic: "serious lifter, calm tool"
// Type: Archivo Narrow (display) + Inter (UI) + JetBrains Mono (data)
// Color: near-black with a single electric accent

// Accent variants — chosen to cover a broad hue span while keeping similar
// lightness/chroma so surrounding UI stays legible (see DEC-theme-accent-cssvar).

import React from 'react';

const LL_ACCENTS = {
  acid:    'oklch(0.86 0.19 132)',  // default — acid green
  amber:   'oklch(0.82 0.18 85)',   // warm amber
  cobalt:  'oklch(0.72 0.20 245)',  // deep blue
  magenta: 'oklch(0.72 0.25 340)',  // electric magenta
};

// Darker accent shades for text on the light theme (≈4.5–5:1 on white).
const LL_ACCENT_TEXT_LIGHT = {
  acid:    'oklch(0.50 0.16 132)',
  amber:   'oklch(0.52 0.14 70)',
  cobalt:  'oklch(0.50 0.19 250)',
  magenta: 'oklch(0.52 0.22 340)',
};

const LL = {
  // Surfaces — themeable via CSS vars (applyTheme sets them per dark/light/oled).
  // The literal here is the DARK value, kept as the var fallback so first paint
  // (before applyTheme runs) is correct. Lightness-stepped, tiny chroma.
  bg:         'var(--ll-bg, oklch(0.17 0.004 250))',        // canvas
  surface:    'var(--ll-surface, oklch(0.22 0.005 250))',   // cards
  surface2:   'var(--ll-surface2, oklch(0.26 0.006 250))',  // raised
  surface3:   'var(--ll-surface3, oklch(0.31 0.007 250))',  // hover / pill
  line:       'var(--ll-line, oklch(0.33 0.006 250))',      // hairline
  line2:      'var(--ll-line2, oklch(0.42 0.008 250))',

  // Foreground
  fg:         'var(--ll-fg, oklch(0.97 0.003 250))',
  fgMute:     'var(--ll-fgMute, oklch(0.72 0.006 250))',
  fgDim:      'var(--ll-fgDim, oklch(0.55 0.006 250))',
  fgFaint:    'var(--ll-fgFaint, oklch(0.42 0.006 250))',

  // Accent — routed through a CSS var so ThemeSettings can flip it live.
  // Fallback keeps default acid green when the var is unset (initial paint).
  accent:     'var(--ll-accent, oklch(0.86 0.19 132))',
  accentDim:  'oklch(0.72 0.16 132)',
  accentInk:  'oklch(0.18 0.02 132)',   // text ON accent
  // Accent used AS text/icon color. Equals the accent on dark themes; on the
  // light theme it is a darker shade (≈4.8:1 on white) — never use LL.accent
  // for text, it is ~1.3:1 on light surfaces.
  accentText: 'var(--ll-accentText, var(--ll-accent, oklch(0.86 0.19 132)))',
  // Accent at low strength for tinted fills (selected rows, chips, badges).
  accentSoft: 'color-mix(in oklab, var(--ll-accent, oklch(0.86 0.19 132)) 14%, transparent)',
  prText:     'var(--ll-prText, oklch(0.82 0.18 85))',

  // Semantic (shared chroma with accent where possible)
  pr:         'oklch(0.82 0.18 85)',    // PR amber
  warn:       'oklch(0.78 0.17 55)',
  danger:     'oklch(0.72 0.19 25)',
  info:       'oklch(0.78 0.12 230)',

  // Type scale (px). Nothing below 11 — gym-distance legibility.
  fs: { cap: 11, sm: 12, meta: 13, body: 15, lg: 17, h3: 20, h2: 28, h1: 34, hero: 48, timer: 72,
    // Named in-between sizes: btn = primary button labels and form inputs
    // (16px also stops iOS zooming into a focused field), stat = numbers in
    // stat tiles, big = the headline number on a summary / chart card.
    btn: 16, stat: 22, big: 40 },
  // Radius scale: bars/ticks, chips/inputs/wells, cards/rows/buttons, sheets;
  // pill for FABs/pills/circles.
  r: { xs: 4, sm: 8, md: 14, lg: 22, pill: 999 },
  // Top padding of every page header (tab roots and sub-pages share one y).
  pageTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
  // Motion
  ease: 'cubic-bezier(.2,.8,.2,1)',
  spring: 'cubic-bezier(.34,1.56,.64,1)',
  // Durations (ms): fast = taps/colour, base = most transitions, slow = sheets/pushes.
  motion: { fast: 120, base: 200, slow: 280 },
  // Spacing scale (px) for paddings, gaps and margins.
  sp: { xxs: 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 },
  // Elevation, themeable (applyTheme swaps in softer, cooler shadows on light).
  //   sm: knobs, small floating chips · md: popovers, toasts, floating buttons
  //   sheet: bottom sheets and dialogs (casts upward) · lift: a pressed/raised card
  shadow: {
    sm: 'var(--ll-shadow-sm, 0 2px 6px rgba(0,0,0,0.25))',
    md: 'var(--ll-shadow-md, 0 8px 24px rgba(0,0,0,0.35))',
    sheet: 'var(--ll-shadow-sheet, 0 -20px 60px rgba(0,0,0,0.5))',
    lift: 'var(--ll-shadow-lift, 0 14px 40px rgba(0,0,0,0.45))',
  },

  // Font stacks
  display: '"Archivo Narrow", "Archivo", Helvetica, Arial, sans-serif',
  ui:      '"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  mono:    '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Global font loader — call once from the host HTML.
function LLFonts() {
  return (
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo+Narrow:wght@400;500;600;700&family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" />
  );
}

// Small pill for muscle tags, set types, etc.
function LLChip({ children, tone = 'default', size = 'md', style = {} }) {
  const tones = {
    default: { bg: LL.surface3, fg: LL.fgMute, br: 'transparent' },
    accent:  { bg: LL.accent,   fg: LL.accentInk, br: 'transparent' },
    outline: { bg: 'transparent', fg: LL.fgMute, br: LL.line2 },
    warm:    { bg: 'color-mix(in oklab, ' + LL.pr + ' 18%, transparent)', fg: LL.pr, br: 'transparent' },
    ghost:   { bg: 'transparent', fg: LL.fgDim, br: 'transparent' },
  };
  const t = tones[tone] || tones.default;
  const sizes = {
    sm: { h: 20, fs: 11, px: 8, ls: 0.1 },
    md: { h: 24, fs: 12, px: 10, ls: 0.1 },
    lg: { h: 28, fs: 12, px: 12, ls: 0.1 },
  };
  const s = sizes[size];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      height: s.h, padding: `0 ${s.px}px`,
      borderRadius: LL.r.pill,
      background: t.bg, color: t.fg,
      border: t.br === 'transparent' ? 'none' : `1px solid ${t.br}`,
      fontFamily: LL.ui, fontSize: s.fs, fontWeight: 600,
      letterSpacing: s.ls,
      whiteSpace: 'nowrap',
      ...style,
    }}>{children}</span>
  );
}

// The one switch visual (51×31 track, white knob). Purely presentational: the
// caller owns the button / role="switch" so it can sit inside a larger row.
function LLSwitchTrack({ on }) {
  return (
    <span aria-hidden="true" style={{
      width: 51, height: 31, borderRadius: 16, flexShrink: 0, position: 'relative', display: 'inline-block',
      background: on ? LL.accent : LL.surface3, transition: `background 200ms ${LL.ease}`,
      boxShadow: on ? 'none' : `inset 0 0 0 1px ${LL.line}`,
    }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 22 : 2, width: 27, height: 27, borderRadius: LL.r.md,
        background: 'oklch(0.99 0 0)', boxShadow: '0 2px 6px rgba(0,0,0,0.25)', transition: `left 220ms ${LL.spring}`,
      }} />
    </span>
  );
}

// The one selection glyph: a 22px filled accent circle with an ink check when
// selected, a 22px line2 ring when not. Presentational (the row is the button).
function LLCheck({ on, size = 22, style = {} }) {
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: LL.r.pill, flexShrink: 0, boxSizing: 'border-box',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: on ? LL.accent : 'transparent', color: LL.accentInk,
      border: on ? 'none' : `1.5px solid ${LL.line2}`,
      transition: `background ${LL.motion.fast}ms ${LL.ease}, border-color ${LL.motion.fast}ms ${LL.ease}`,
      ...style,
    }}>
      {on && <svg width={size * 0.64} height={size * 0.64} viewBox="0 0 20 20" fill="none"><path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </span>
  );
}

// The one close button: a 32px surface2 circle with an fgMute ✕, inside a 44px
// hit area (negative margin keeps the visual alignment).
function LLClose({ onClick, label = 'Close', style = {} }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} style={{
      width: 44, height: 44, margin: -6, padding: 0, flexShrink: 0, border: 'none', background: 'transparent',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', ...style,
    }}>
      <span style={{ width: 32, height: 32, borderRadius: LL.r.pill, background: LL.surface2, color: LL.fgMute, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5.5 5.5l9 9M14.5 5.5l-9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
      </span>
    </button>
  );
}

// Big number readout — display font, tabular nums
function LLNum({ value, unit, size = 44, color = LL.fg, style = {} }) {
  return (
    <span style={{
      fontFamily: LL.display, fontWeight: 700,
      fontSize: size, lineHeight: 1,
      color, letterSpacing: -0.5,
      fontVariantNumeric: 'tabular-nums',
      display: 'inline-flex', alignItems: 'baseline', gap: 4,
      ...style,
    }}>
      {value}
      {unit && <span style={{ fontSize: size * 0.35, fontWeight: 500, color: LL.fgDim, letterSpacing: 0.3, fontFamily: LL.ui, textTransform: 'uppercase' }}>{unit}</span>}
    </span>
  );
}

// Section kicker label
function LLKicker({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: LL.ui, fontSize: 11, fontWeight: 700,
      color: LL.fgDim, letterSpacing: 1.2,
      textTransform: 'uppercase',
      ...style,
    }}>{children}</div>
  );
}

// Icon set — hand-built line icons, 24px grid
const LLIcon = {
  plus:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  minus:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 10h12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  check:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 10.5l4 4 8-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  x:       (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>,
  chev:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M7 5l6 5-6 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevDown:(p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M5 7l5 6 5-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevUp:  (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M5 13l5-6 5 6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  back:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M12 5l-5 5 5 5M7 10h9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  play:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" {...p}><path d="M6 4.5v11l9-5.5-9-5.5z"/></svg>,
  pause:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" {...p}><rect x="5.5" y="4.5" width="3" height="11" rx="0.5"/><rect x="11.5" y="4.5" width="3" height="11" rx="0.5"/></svg>,
  timer:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="11" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M10 8v3l2 1.5M8 3h4M10 5V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  note:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 4h12v12l-3 0-3 0-6 0V4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M7 8h6M7 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  video:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="2.5" y="5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M13.5 9l4-2v6l-4-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  more:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" {...p}><circle cx="4.5" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="15.5" cy="10" r="1.5"/></svg>,
  settings:(p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  home:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 9l7-5.5L17 9v8h-5v-5H8v5H3V9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  dumbbell:(p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="1.5" y="7.5" width="2.5" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="16" y="7.5" width="2.5" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.5"/><rect x="4" y="9" width="12" height="2" stroke="currentColor" strokeWidth="1.5"/></svg>,
  chart:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 15V5M3 15h14M6 12v-2M10 12V7M14 12V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  calendar:(p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="3" y="4" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 8h14M7 2v4M13 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  flame:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 2c1 3 4 4 4 8a4 4 0 01-8 0c0-1.5.5-2.5 1.5-3.5 0 2 1 3 2 3 0-2-1-4 .5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  up:      (p) => <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...p}><path d="M6 2l5 5H8v3H4V7H1l5-5z" fill="currentColor"/></svg>,
  swap:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 7h11l-3-3M16 13H5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  grip:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" {...p}><circle cx="7" cy="5" r="1.3"/><circle cx="13" cy="5" r="1.3"/><circle cx="7" cy="10" r="1.3"/><circle cx="13" cy="10" r="1.3"/><circle cx="7" cy="15" r="1.3"/><circle cx="13" cy="15" r="1.3"/></svg>,
  lock:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="4" y="9" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M7 9V6a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  ai:      (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 3l1.8 4.2L16 9l-4.2 1.8L10 15l-1.8-4.2L4 9l4.2-1.8L10 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M15 14l.8 1.7L17.5 16.5l-1.7.8L15 19l-.8-1.7L12.5 16.5l1.7-.8L15 14z" fill="currentColor"/></svg>,
  upRight: (p) => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}><path d="M4 10l6-6M5 4h5v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  body:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="4" r="2" stroke="currentColor" strokeWidth="1.5"/><path d="M6 17v-5l-2-4M14 17v-5l2-4M10 7v4M8 11h4v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  camera:  (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="2.5" y="5.5" width="15" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="11" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M7 5.5l1.5-2h3l1.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  users:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 17c0-3 2.7-5 6-5s6 2 6 5M14 6.5a2.5 2.5 0 110 5M14 12.5c2.5.3 4 1.9 4 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  heart:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M10 16.5S3 12 3 7.5a3.5 3.5 0 017-1 3.5 3.5 0 017 1c0 4.5-7 9-7 9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  cloud:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M14 15H6a3 3 0 010-6 4 4 0 017.9-1A3 3 0 0114 15z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
  watch:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="5" y="5" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M7 5V3h6v2M7 15v2h6v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M10 8v3l1.5 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  trend:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 14l4-4 3 3 7-8M13 5h4v4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  target:  (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6"/><circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="1.6"/><circle cx="10" cy="10" r="0.9" fill="currentColor"/></svg>,
  trophy:  (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M6 3.5h8v4a4 4 0 01-8 0v-4zM6 5H3.5v1.5A2.5 2.5 0 006 9M14 5h2.5v1.5A2.5 2.5 0 0114 9M10 11.5V14M7 16.5h6M8 14h4v2.5H8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/></svg>,
  plateau: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 14l4-5h10" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 17h14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".45"/></svg>,
  barsDown:(p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 16V9M8 16v-5M12 16v-3" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/><path d="M14.5 4v6m0 0l-2-2m2 2l2-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  link:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M8.5 11.5a3 3 0 004.2 0l2.6-2.6a3 3 0 00-4.2-4.2l-1 1M11.5 8.5a3 3 0 00-4.2 0l-2.6 2.6a3 3 0 004.2 4.2l1-1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>,
  sliders: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 6h8M15 6h2M3 14h2M9 14h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/><circle cx="13" cy="6" r="2" stroke="currentColor" strokeWidth="1.6"/><circle cx="7" cy="14" r="2" stroke="currentColor" strokeWidth="1.6"/></svg>,
  trash:   (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M4 6h12M8 6V4.5h4V6M6 6l.7 10h6.6L14 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  search:  (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="1.7"/><path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>,
  send:    (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 10l14-7-4 14-3-6-7-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
};

// Surface/foreground palettes per theme. Dark is the var fallback (in LL above)
// so it needs no entry — applyTheme just clears the overrides for dark.
const LL_THEME_KEYS = [
  '--ll-bg', '--ll-surface', '--ll-surface2', '--ll-surface3', '--ll-line', '--ll-line2',
  '--ll-fg', '--ll-fgMute', '--ll-fgDim', '--ll-fgFaint',
];
const LL_THEME_VARS = {
  light: {
    '--ll-bg':       'oklch(0.95 0.004 250)',
    '--ll-surface':  'oklch(0.99 0.002 250)',
    '--ll-surface2': 'oklch(0.965 0.003 250)',
    '--ll-surface3': 'oklch(0.92 0.005 250)',
    '--ll-line':     'oklch(0.86 0.006 250)',
    '--ll-line2':    'oklch(0.78 0.008 250)',
    '--ll-fg':       'oklch(0.22 0.012 250)',
    '--ll-fgMute':   'oklch(0.44 0.010 250)',
    '--ll-fgDim':    'oklch(0.56 0.008 250)',
    '--ll-fgFaint':  'oklch(0.66 0.006 250)',
  },
  oled: {
    '--ll-bg':       'oklch(0 0 0)',
    '--ll-surface':  'oklch(0.15 0.004 250)',
    '--ll-surface2': 'oklch(0.20 0.005 250)',
    '--ll-surface3': 'oklch(0.26 0.006 250)',
    '--ll-line':     'oklch(0.28 0.006 250)',
    '--ll-line2':    'oklch(0.38 0.008 250)',
    '--ll-fg':       'oklch(0.98 0.003 250)',
    '--ll-fgMute':   'oklch(0.72 0.006 250)',
    '--ll-fgDim':    'oklch(0.55 0.006 250)',
    '--ll-fgFaint':  'oklch(0.42 0.006 250)',
  },
};
const LL_SHADOW_LIGHT = {
  '--ll-shadow-sm': '0 1px 3px rgba(16,24,40,0.12)',
  '--ll-shadow-md': '0 8px 24px rgba(16,24,40,0.12)',
  '--ll-shadow-sheet': '0 -12px 40px rgba(16,24,40,0.14)',
  '--ll-shadow-lift': '0 12px 32px rgba(16,24,40,0.16)',
};
// Canvas color used for <body> + meta theme-color per theme (the var fallback
// for dark; explicit for light/oled). Keeps the area behind the app in-theme.
const LL_THEME_BG = {
  dark:  'oklch(0.17 0.004 250)',
  light: 'oklch(0.95 0.004 250)',
  oled:  'oklch(0 0 0)',
};

// Runtime theme/accent propagation — writes CSS vars on <html> so every LL.*
// color (which reads var(--ll-*, …)) repaints live, no reload. Called by
// ThemeInjector whenever state.prefs changes.
function applyTheme(prefs) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Accent
  const accentKey = (prefs && prefs.accent) || 'acid';
  root.style.setProperty('--ll-accent', LL_ACCENTS[accentKey] || LL_ACCENTS.acid);
  const isLight = ((prefs && prefs.theme) || 'dark') === 'light';
  if (isLight) {
    root.style.setProperty('--ll-accentText', LL_ACCENT_TEXT_LIGHT[accentKey] || LL_ACCENT_TEXT_LIGHT.acid);
    root.style.setProperty('--ll-prText', 'oklch(0.56 0.15 70)');
  } else {
    root.style.removeProperty('--ll-accentText');
    root.style.removeProperty('--ll-prText');
  }

  // Theme — set the surface/foreground vars (or clear them for dark → fallback).
  const theme = (prefs && prefs.theme) || 'dark';
  root.setAttribute('data-ll-theme', theme);
  const vars = LL_THEME_VARS[theme];
  if (vars) {
    LL_THEME_KEYS.forEach((k) => root.style.setProperty(k, vars[k]));
  } else {
    LL_THEME_KEYS.forEach((k) => root.style.removeProperty(k));
  }
  // Elevation: light surfaces get soft cool-grey shadows; dark/OLED keep the
  // deep fallbacks in LL.shadow.
  Object.entries(LL_SHADOW_LIGHT).forEach(([k, v]) => {
    if (isLight) root.style.setProperty(k, v); else root.style.removeProperty(k);
  });

  // Native form controls / scrollbars follow the theme; --fg keeps App.jsx's
  // base text color in step. The <body> canvas reads var(--ll-bg) (set in
  // index.html), so it repaints from the var above — no direct body write here.
  const bg = LL_THEME_BG[theme] || LL_THEME_BG.dark;
  root.style.colorScheme = theme === 'light' ? 'light' : 'dark';
  root.style.setProperty('--fg', (vars ? vars['--ll-fg'] : 'oklch(0.97 0.003 250)'));
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', bg);
}

// Desktop / tablet layout breakpoint: at ≥ 900px wide the shell shows a side
// rail instead of the bottom tab bar, flows sit in a centered column and the
// dashboards go two-column. Below it nothing changes (the phone layout).
export const LL_WIDE = 900;
const WIDE_QUERY = `(min-width: ${LL_WIDE}px)`;
export function useWide() {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(WIDE_QUERY).matches;
  const [wide, setWide] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const m = window.matchMedia(WIDE_QUERY);
    const on = () => setWide(m.matches);
    on();
    m.addEventListener ? m.addEventListener('change', on) : m.addListener(on);
    return () => { m.removeEventListener ? m.removeEventListener('change', on) : m.removeListener(on); };
  }, []);
  return wide;
}

export { LL, LL_ACCENTS, LL_THEME_VARS, LLFonts, LLChip, LLNum, LLKicker, LLIcon, LLSwitchTrack, LLCheck, LLClose, applyTheme };
